/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import { PreLoading, Button } from "@tripian/react";
import { MY_FEEDBACK } from "../../constants/ROUTER_PATH_TITLE";
import { useNavigate } from "react-router-dom";
import { api } from "@tripian/core";
import AppNav from "../../App/AppNav/AppNav";
import Model from "@tripian/model";
import useTranslate from "../../hooks/useTranslate";
import useFeedback from "../../hooks/useFeedback";

const MyFeedback = () => {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { userFeedbacks, feedbackUpdate, feedbackReply, loadingUserFeedbacks } =
    useFeedback();

  document.title = MY_FEEDBACK.TITLE(t("user.myFeedback.title"));
  const [userFeed, setUserFeed] = useState<Model.UserFeedback[]>([]);
  const [selectedFeedbackIndex, setSelectedFeedbackIndex] = useState<
    number | null
  >(null);
  const [replyMessage, setReplyMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const contentPanelRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    // Use the userFeedbacks function from the useFeedback hook
    userFeedbacks().then(u => {
      setUserFeed(u);
      // No longer automatically selecting a feedback when the page loads
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to bottom of messages when selected feedback changes or replies update
  useEffect(() => {
    if (selectedFeedbackIndex !== null && messagesContainerRef.current) {
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [selectedFeedbackIndex, userFeed]);

  // Handle selecting a feedback item
  const handleFeedbackSelect = async (index: number) => {
    setSelectedFeedbackIndex(index);

    // Get the selected feedback
    const selectedFeedback = userFeed[index];

    // If the feedback is unread, mark it as read
    if (selectedFeedback && !selectedFeedback.is_read) {
      try {
        // Call the feedbackUpdate function to mark as read
        const updatedFeedbacks = await feedbackUpdate(
          selectedFeedback.id,
          true
        );
        setUserFeed(updatedFeedbacks);
      } catch (error) {
        console.error("Error marking feedback as read:", error);
      }
    }

    // Add a visual cue for mobile users
    if (isMobile) {
      // Briefly highlight the item to indicate it was selected
      const element = document.getElementById(`feedback-item-${index}`);
      if (element) {
        element.classList.add("bg-blue-100");
        setTimeout(() => {
          element.classList.remove("bg-blue-100");
        }, 300);
      }
    }
  };

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint is 768px in Tailwind
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Scroll to content panel when a feedback is selected on mobile
  useEffect(() => {
    if (selectedFeedbackIndex !== null && isMobile && contentPanelRef.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        contentPanelRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [selectedFeedbackIndex, isMobile]);

  // Format date to readable format
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString.split("T")[0];
    }
  };

  // Handle sending a reply
  const handleSendReply = async () => {
    if (!replyMessage.trim() || selectedFeedbackIndex === null) return;

    setIsSending(true);

    // Store the message content
    const messageContent = replyMessage;
    setReplyMessage(""); // Clear input immediately for better UX

    // Create a copy of user feedback array for temporary display
    const updatedFeedback = [...userFeed];
    const feedback = updatedFeedback[selectedFeedbackIndex];

    // Create a unique temporary ID for this reply
    const tempReplyId = `temp-${Date.now()}`;

    // Add the temporary reply immediately to the UI
    if (!feedback.replies) {
      feedback.replies = [];
    }

    feedback.replies.push({
      id: tempReplyId,
      sender: "user",
      reply: messageContent,
      created_at: new Date().toISOString(),
      isTemp: true, // Mark as temporary
    });

    // Update state to show the message immediately
    setUserFeed(updatedFeedback);

    try {
      // Call the actual API to send the reply
      const replyRequest: Model.FeedbackReplyRequest = {
        feedbackId: feedback.id,
        reply: messageContent,
        sender: "user",
      };

      // Get the updated feedbacks from the API
      const updatedFeedbacks = await feedbackReply(replyRequest);
      setUserFeed(updatedFeedbacks);
    } catch (error) {
      console.error("Error sending reply:", error);

      // If the API call fails, remove the temporary message
      const revertedFeedback = [...updatedFeedback];
      const revertedFeedbackItem = revertedFeedback[selectedFeedbackIndex];

      if (revertedFeedbackItem && revertedFeedbackItem.replies) {
        // Find and remove the temporary message
        revertedFeedbackItem.replies = revertedFeedbackItem.replies.filter(
          r => r.id !== tempReplyId
        );
        setUserFeed(revertedFeedback);
      }

      // Restore the message to the input field
      setReplyMessage(messageContent);

      // Show an error message to the user
      alert(t("user.myFeedback.errorSendingReply"));
    } finally {
      setIsSending(false);
    }
  };

  if (loadingUserFeedbacks) {
    return (
      <>
        <AppNav header={MY_FEEDBACK.HEADER?.(t("user.myFeedback.title"))} />
        <PreLoading />
      </>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={
        window.tconfig.IMAGE_PATHS.APP_BACKGROUND_IMG_URL
          ? {
              backgroundImage: `url(${window.tconfig.IMAGE_PATHS.APP_BACKGROUND_IMG_URL})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}
      }
    >
      <header className="bg-white shadow-sm">
        <AppNav header={MY_FEEDBACK.HEADER?.(t("user.myFeedback.title"))} />
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex flex-col items-center md:items-start space-y-2 mb-4 md:mb-0">
              <h1 className="text-2xl font-bold text-gray-800 text-center md:text-left">
                {t("user.myFeedback.title")}
              </h1>
              <p className="text-sm text-gray-500">
                {userFeed.length > 0
                  ? t("user.myFeedback.feedbackCount")
                      .replace("%s%", userFeed.length.toString())
                      .replace(
                        "%s%",
                        userFeed.length === 1
                          ? t("user.myFeedback.reply")
                          : t("user.myFeedback.replies")
                      )
                  : t("user.myFeedback.noFeedbacksYet")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/feedback")}
              color="primary"
              text={t("user.myFeedback.createFeedback")}
            />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-0 py-4">
        {userFeed.length === 0 ? (
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-700">
              {t("user.myFeedback.warning")}
            </h3>
            <p className="mt-2 text-gray-500">
              {t("user.myFeedback.noFeedbackMessage")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 w-full">
            {/* Left Panel - Feedback List */}
            <div className="md:col-span-3 bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-primary-color"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <h2 className="font-semibold text-gray-800">
                    {t("user.myFeedback.yourFeedbacks")}
                  </h2>
                </div>
                {userFeed.filter(f => !f.is_read).length > 0 && (
                  <span className="bg-primary-color text-white text-xs font-medium px-2 py-1 rounded-full">
                    {t("user.myFeedback.unreadCount").replace(
                      "%s%",
                      userFeed.filter(f => !f.is_read).length.toString()
                    )}
                  </span>
                )}
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: "75vh" }}>
                {userFeed.length === 0 ? (
                  <div className="p-6 text-center">
                    <svg
                      className="w-12 h-12 mx-auto text-gray-300 mb-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                    <p className="text-gray-500">
                      {t("user.myFeedback.warning")}
                    </p>
                  </div>
                ) : (
                  userFeed.map((feedback, index) => (
                    <div
                      key={`feedback-item-${index}`}
                      className={`relative transition-all duration-200 cursor-pointer ${
                        selectedFeedbackIndex === index
                          ? "bg-blue-50 border-l-4 border-l-primary-color"
                          : "hover:bg-gray-50 border-l-4 border-transparent"
                      }`}
                      onClick={() => handleFeedbackSelect(index)}
                      id={`feedback-item-${index}`}
                    >
                      <div className="p-4">
                        {/* Okunmamış göstergesi - kartın sağ üst köşesinde yuvarlak nokta */}
                        {!feedback.is_read && (
                          <span className="absolute top-3 right-3 inline-flex h-2.5 w-2.5 rounded-full bg-primary-color animate-pulse"></span>
                        )}

                        {/* Feedback başlığı ve tarihi */}
                        <div className="mb-2">
                          <h3
                            className={`font-medium truncate pr-4 ${!feedback.is_read ? "text-gray-900" : "text-gray-700"}`}
                          >
                            {feedback.subject_type === "main"
                              ? t("user.myFeedback.generalFeedback")
                              : feedback.subject_type}
                          </h3>
                          <div className="flex items-center mt-1 mb-1.5">
                            <span
                              className={`text-xs ${!feedback.is_read ? "text-gray-700" : "text-gray-500"}`}
                            >
                              {formatDate(feedback.created_at)}
                            </span>
                          </div>
                        </div>

                        {/* Feedback içeriği */}
                        <p
                          className={`text-sm line-clamp-2 mb-2 ${!feedback.is_read ? "text-gray-800" : "text-gray-600"}`}
                        >
                          {feedback.desc}
                        </p>

                        {/* Alt bilgiler - kategori ve durum */}
                        <div className="flex items-center justify-between mt-2">
                          {/* Durumu göstergesi */}
                          <div className="flex items-center">
                            {feedback.replies && feedback.replies.length > 0 ? (
                              <span className="flex items-center text-xs text-green-600 font-medium">
                                <svg
                                  className="w-4 h-4 mr-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                  />
                                </svg>
                                {feedback.replies.length +
                                  " " +
                                  t(
                                    feedback.replies.length === 1
                                      ? "user.myFeedback.reply"
                                      : "user.myFeedback.replies"
                                  )}
                              </span>
                            ) : (
                              <span className="flex items-center text-xs text-gray-500">
                                <svg
                                  className="w-4 h-4 mr-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                {!feedback.is_read
                                  ? t("user.myFeedback.waiting")
                                  : t("user.myFeedback.noReply")}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Mobil için aşağı ok göstergesi */}
                        {isMobile && selectedFeedbackIndex === index && (
                          <div className="mt-2 flex justify-center">
                            <svg
                              className="w-5 h-5 text-primary-color animate-bounce"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                              />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Feedback'ler arasındaki çizgi */}
                      <div
                        className={`h-px bg-gray-100 mx-4 ${selectedFeedbackIndex === index ? "hidden" : ""}`}
                      ></div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right Panel - Feedback Detail */}
            <div
              ref={contentPanelRef}
              className="md:col-span-9 bg-white rounded-lg shadow-sm overflow-hidden"
            >
              {/* Show a message when no feedback is selected */}
              {selectedFeedbackIndex === null ? (
                <div className="flex flex-col items-center justify-center h-full py-20 px-6 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-10 h-10 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-700 mb-2">
                    {t("user.myFeedback.selectFeedback")}
                  </h3>
                  <p className="text-gray-500 max-w-md">
                    {t("user.myFeedback.selectFeedbackInstruction")}
                  </p>
                </div>
              ) : (
                <>
                  {/* Mobile indicator to show this is the content area */}
                  {isMobile && (
                    <div className="bg-blue-50 py-2 px-4 border-b border-blue-100 md:hidden">
                      <div className="flex items-center text-sm text-primary-color">
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>
                          {t("user.myFeedback.showingSelectedFeedback")}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Feedback content */}
                  {/* Display the selected feedback content here */}
                  {userFeed[selectedFeedbackIndex] && (
                    <>
                      {/* Feedback header */}
                      <div className="p-5 border-b border-gray-100">
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="px-3 py-1 bg-primary-color text-white text-xs font-medium rounded-full">
                                {userFeed[selectedFeedbackIndex].subject_title}
                              </span>
                              <span className="text-sm text-gray-500">
                                {formatDate(
                                  userFeed[selectedFeedbackIndex].created_at
                                )}
                              </span>
                            </div>
                            {!userFeed[selectedFeedbackIndex].is_read && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                {t("user.myFeedback.unreadStatus")}
                              </span>
                            )}
                          </div>
                          <h2 className="text-xl font-bold text-gray-800">
                            {userFeed[selectedFeedbackIndex].subject_type ===
                            "main"
                              ? t("user.myFeedback.generalFeedback")
                              : userFeed[selectedFeedbackIndex].subject_type}
                          </h2>
                        </div>
                      </div>

                      {/* Feedback content */}
                      <div
                        className="p-6 bg-gray-50 flex flex-col"
                        style={{ height: "calc(100vh - 350px)" }}
                      >
                        <div
                          className="flow-root overflow-y-auto flex-grow mb-4"
                          style={{ scrollBehavior: "smooth" }}
                          id="messagesContainer"
                          ref={messagesContainerRef}
                        >
                          <ul className="-mb-2">
                            {/* User's original feedback message */}
                            <li>
                              <div className="relative pb-6">
                                {userFeed[selectedFeedbackIndex].replies &&
                                  userFeed[selectedFeedbackIndex].replies
                                    .length > 0 && (
                                    <span
                                      className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                                      aria-hidden="true"
                                    ></span>
                                  )}
                                <div className="relative flex items-start space-x-3">
                                  {/* Avatar */}
                                  <div className="relative">
                                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center ring-4 ring-white text-gray-500">
                                      <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                        />
                                      </svg>
                                    </div>
                                  </div>

                                  {/* Content */}
                                  <div className="w-full max-w-[90%]">
                                    <div className="flex justify-between items-end mb-1">
                                      <span className="text-sm font-medium text-gray-900">
                                        {t("user.myFeedback.you")}
                                      </span>
                                    </div>
                                    <div className="bg-primary-color text-white px-4 py-3 rounded-lg rounded-tl-none shadow-sm">
                                      <p className="text-sm">
                                        {userFeed[selectedFeedbackIndex].desc}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </li>

                            {/* Replies */}
                            {userFeed[selectedFeedbackIndex].replies &&
                              userFeed[selectedFeedbackIndex].replies.map(
                                (reply, replyIndex) => (
                                  <li key={`reply-${replyIndex}`}>
                                    <div className="relative pb-6">
                                      {replyIndex <
                                        userFeed[selectedFeedbackIndex].replies
                                          .length -
                                          1 && (
                                        <span
                                          className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                                          aria-hidden="true"
                                        ></span>
                                      )}
                                      <div className="relative flex items-start space-x-3">
                                        {/* Avatar - changes based on sender */}
                                        <div className="relative">
                                          <div
                                            className={`h-10 w-10 rounded-full ${
                                              reply.sender === "user"
                                                ? "bg-gray-200 text-gray-500"
                                                : "bg-primary-color text-white"
                                            } flex items-center justify-center ring-4 ring-white`}
                                          >
                                            {reply.sender === "user" ? (
                                              <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                />
                                              </svg>
                                            ) : (
                                              <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                                />
                                              </svg>
                                            )}
                                          </div>
                                        </div>

                                        {/* Content - changes style based on sender */}
                                        <div className="w-full max-w-[90%]">
                                          <div className="flex justify-between items-end mb-1">
                                            <span className="text-sm font-medium text-gray-900">
                                              {reply.sender === "user"
                                                ? t("user.myFeedback.you")
                                                : t(
                                                    "user.myFeedback.supportTeam"
                                                  )}
                                            </span>
                                            <div className="flex items-center">
                                              {reply.isTemp && (
                                                <span className="text-xs text-yellow-600 italic mr-2">
                                                  {t("user.myFeedback.sending")}
                                                </span>
                                              )}
                                              <span className="text-xs text-gray-500">
                                                {formatDate(reply.created_at)}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className={`${
                                              reply.sender === "user"
                                                ? "bg-primary-color text-white"
                                                : "bg-white text-gray-800 border border-gray-200"
                                            } px-4 py-3 rounded-lg ${reply.sender === "user" ? "rounded-tr-none" : "rounded-tl-none"} shadow-sm ${reply.isTemp ? "opacity-70" : ""}`}
                                          >
                                            <p className="text-sm">
                                              {reply.reply}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </li>
                                )
                              )}

                            {/* No reply indicator */}
                            {(!userFeed[selectedFeedbackIndex].replies ||
                              userFeed[selectedFeedbackIndex].replies.length ===
                                0) && (
                              <li>
                                <div className="mt-4 flex justify-center">
                                  <div className="bg-white border border-dashed border-gray-300 rounded-lg px-6 py-4 flex items-center justify-center max-w-md">
                                    <div className="flex items-center text-gray-500">
                                      <svg
                                        className="w-5 h-5 mr-2"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={1.5}
                                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>
                                        {!userFeed[selectedFeedbackIndex]
                                          .is_read
                                          ? t(
                                              "user.myFeedback.feedbackReceivedMessage"
                                            )
                                          : t("user.myFeedback.noResponseYet")}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* Message input area */}
                        <div className="mt-auto">
                          <div className="flex items-start space-x-3">
                            <div className="relative">
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center ring-4 ring-white text-gray-500">
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="relative">
                                <textarea
                                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-color focus:border-transparent resize-none shadow-sm"
                                  placeholder={t(
                                    "user.myFeedback.writeReplyPlaceholder"
                                  )}
                                  rows={3}
                                  value={replyMessage}
                                  onChange={e =>
                                    setReplyMessage(e.target.value)
                                  }
                                  onKeyDown={e => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSendReply();
                                    }
                                  }}
                                ></textarea>
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-2 space-y-3 sm:space-y-0 sm:space-x-4">
                                  <div className="text-xs text-gray-500">
                                    {t("user.myFeedback.replyWillBeSent")}
                                  </div>
                                  <Button
                                    onClick={handleSendReply}
                                    color="primary"
                                    disabled={isSending || !replyMessage.trim()}
                                    text={
                                      isSending
                                        ? t("user.myFeedback.sending")
                                        : t("user.myFeedback.sendMessage")
                                    }
                                    className="self-end sm:self-auto text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyFeedback;
